import {
  TicketSaleLiteModel,
  TicketSaleModel,
  useGetTicketSale,
  useGetTicketSales,
} from 'api-hooks/sale/ticket-sale';
import { OnChangeParams, Option } from 'baseui/select';
import { SaleStatusEnum } from 'common/constants/enum';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: TicketSaleLiteModel | TicketSaleModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.invoiceNumber, item.code, item.customer.name]),
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterSelectItem?: (prevValue?: any, value?: any) => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: TicketSaleModel) => void;
  noMargin?: boolean;
  disabled?: boolean;
  isSales?: boolean;
}

export default function TicketSaleInvoiceSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:ticket_sale'),
    placeholder = t('common:select_ticket_sale'),
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (_value) => {
      setValue(props.name, _value[0]?.id, {
        shouldTouch: true,
        shouldValidate: true,
      });
      props.onAfterSelectItem?.(value, _value[0]?.id);
    },
    useListQueryHook: useGetTicketSales,
    useDetailLazyQueryHook: useGetTicketSale,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter: {
            status: props.isSales
              ? `${SaleStatusEnum.Active}`
              : `${SaleStatusEnum.Active}|${SaleStatusEnum.OpenTicket}|${SaleStatusEnum.Completed}|${SaleStatusEnum.Waiting}`,
            is_completed: false,
          },
          selection: 'on',
        },
      }),
      [props.isSales],
    ),
    renderCreate: false,
    getMemoizedDetailVariables: React.useCallback(
      (value: any) => ({
        id:
          typeof value === 'object'
            ? Array.isArray(value)
              ? value[0].id
              : value.id
            : value,
      }),
      [],
    ),
    listTransformer(result) {
      return result.data.map((v) => transformer(v));
    },
    detailTransformer(result) {
      const newResult = QueryTransformer(result, TicketSaleModel);
      props.onAfterDetailChange?.(newResult.data);
      return transformer(newResult.data);
    },
    paginationTransformer(result) {
      return result.meta;
    },
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      {...selectProps}
    />
  );
}
